import { useEffect, useState } from 'react'
import { isBrowser } from 'react-device-detect'
import { type NextPage } from 'next'
import NextLink from 'next/link'
import { Stack } from '@dlpco/fluid-layout'
import { Button, Display, Heading, Text } from '@dlpco/ginga-stone'
import { ExclamationIllustration } from 'design-elements/shared/illustrations'
import {
  isPlaygroundMode,
  redirectToLoadUser,
  webviewRuntime
} from 'modules/shared/page-enhancers/src/utils/webview-runtime'
import { ALWAYS_REFRESH_SESSION, appEnv, TIME_TO_EXPIRE_TOKEN } from 'shared/envs'
import { getCookieDomain, isomorphicCookies } from 'shared/storage'

import { authenticator } from '~/domains/platform/core/authenticator/authenticator'
import { Loader } from '~/domains/platform/design-system/loader/loader'
import { logger } from '~/domains/platform/infra/monitoring/logger'
import BlackBird from '~/domains/platform/lib/blackbird'
import { Bucket } from '~/domains/platform/lib/bucket'
import { syncPaymentProfileAccount } from '~/domains/platform/lib/payment-profile-storage'
import { If } from '~/domains/platform/lib/utilities-components'
import { TopBarLayout } from '~/ui/components/layouts/top-bar-layout'
import { CleanPageContainer, ErrorContainer } from '~/ui/containers'
import { useQueryRequest } from '~/ui/hooks/utils'

import { exMachina } from '../core'
import { SHOULD_REDIRECT_TO_KYC_KEY } from '../helpers/compose-session'
import { type Entity, type OptIns, type Subject, type WithDeusExMachinaProps } from '../types'
import { analiticaInstance } from 'shared/analitica'
interface DeusExMachinaProps {
  entity: Entity<OptIns>
  subject: Subject<OptIns>
}

let lock = false

const ACCESS_APP_ROUTE = '/acesse-o-app'
const ACCOUNT_PREPARING_ROUTE = '/preparando-sua-conta'
const ACCOUNT_ACTIVATION_ROUTE = '/ative-sua-conta'
const SELECT_ACCOUNT = '/selecionar-conta'

export const withDeusExMachina = (
  Page: NextPage<Omit<WithDeusExMachinaProps, keyof DeusExMachinaProps>>
): NextPage<Omit<WithDeusExMachinaProps, keyof DeusExMachinaProps>> => {
  function WrappedPage(props: Omit<WithDeusExMachinaProps, keyof DeusExMachinaProps>) {
    const { isSuccess, deusExMachina, isError, error, isWebView } = useDeusExMachina()

    if (isSuccess && deusExMachina) {
      const { stoneAccount } = deusExMachina.entity ?? {}
      const isAccountPreparing =
        stoneAccount?.situation === 'one_stone' &&
        stoneAccount?.hasActiveOffer === false &&
        stoneAccount.kyc?.shouldRedirect === false

      if (
        isAccountPreparing &&
        BlackBird.getPathname() !== ACCOUNT_PREPARING_ROUTE &&
        BlackBird.getPathname() !== SELECT_ACCOUNT
      ) {
        BlackBird.travelTo(ACCOUNT_PREPARING_ROUTE)

        return (
          <CleanPageContainer>
            <Loader />
          </CleanPageContainer>
        )
      }

      return <Page {...deusExMachina} {...props} />
    }

    if (isSuccess && !deusExMachina) {
      const shouldRedirectToKyc = Bucket.session.get(SHOULD_REDIRECT_TO_KYC_KEY) === true
      const route = shouldRedirectToKyc ? ACCOUNT_ACTIVATION_ROUTE : ACCESS_APP_ROUTE

      if (BlackBird.getPathname() === route) {
        return <Page {...(props as WithDeusExMachinaProps)} />
      }

      BlackBird.travelTo(route)

      return (
        <CleanPageContainer>
          <Loader />
        </CleanPageContainer>
      )
    }

    if (isError && isWebView) {
      return <WebviewError error={error} />
    }

    if (isError && !isWebView) {
      const title = error?.response?.status ? `Erro ${error.response.status}` : undefined
      const requestId = error?.response?.headers['x-request-id']
      const caption = requestId && `id:${requestId}`

      return <Error title={title} caption={caption} />
    }

    return (
      <CleanPageContainer>
        <Loader />
      </CleanPageContainer>
    )
  }

  WrappedPage.displayName = `withDeusExMachinaPages(${Page.displayName})`

  return WrappedPage
}
export type UseDeusExMachinaReturn = {
  deusExMachina?: DeusExMachinaProps | null
  isSuccess: boolean
  isError: boolean
  error?: any
  isWebView?: boolean
}

export function useDeusExMachina(): UseDeusExMachinaReturn {
  const [isSessionValid, setIsSessionValid] = useState(authenticator.isSessionValid())
  const { identityId, isWebView } = isomorphicCookies.getAll()

  useEffect(() => {
    const search = new URLSearchParams(window.location.search)
    const webviewQuery = search.get('webview') === 'true'
    if (webviewQuery) {
      lock = true
      redirectToLoadUser()
      setIsSessionValid(true)
    }
  }, [isWebView])

  const {
    data: deusExMachina,
    isSuccess,
    isLoading,
    isError,
    error
  } = useQueryRequest<DeusExMachinaProps | null, unknown, true>(['useDeusExMachina', identityId], () => exMachina(), {
    enabled: isSessionValid,
    staleTime: 300000,
    retry: 3
  })

  deusExMachina?.entity?.id &&
    isomorphicCookies.set('identityId', deusExMachina.entity.id, { maxAge: 60 * 60 * 24 * 30 * 6 })
  deusExMachina?.entity?.paymentAccount?.id &&
    isomorphicCookies.set('accountId', deusExMachina.entity.paymentAccount.id || '', {
      maxAge: 60 * 60 * 24 * 30 * 6
    })
  deusExMachina?.subject?.displayName &&
    isomorphicCookies.set('stn_user_hub', JSON.stringify({ user: { name: deusExMachina?.subject?.displayName } }), {
      domain: getCookieDomain(window.location.href),
      path: '/',
      maxAge: TIME_TO_EXPIRE_TOKEN
    })

  const { stoneAccount } = deusExMachina?.entity || {}
  const hasPaymentProfiles = stoneAccount?.paymentProfiles?.length ?? 0

  const shouldCachePaymentProfiles = !isLoading && !isError && hasPaymentProfiles > 0

  if (shouldCachePaymentProfiles && stoneAccount) {
    Bucket.local.set('stn::payment::profile::hub', stoneAccount)
  } else {
    Bucket.local.remove('stn::payment::profile::hub')
  }

  hasPaymentProfiles &&
    isomorphicCookies.set('stn_payment_profile_hub', JSON.stringify([...(stoneAccount?.paymentProfiles || [])]), {
      domain: getCookieDomain(window.location.href),
      path: '/'
    })

  useEffect(() => {
    const situation = deusExMachina?.entity?.stoneAccount?.situation?.toUpperCase()
    if (!situation) return
    analiticaInstance.updateMetadata(prev => ({ ...prev, situation }))
  }, [deusExMachina])

  const paymentAccountId = deusExMachina?.entity?.paymentAccount?.id
  useEffect(() => {
    syncPaymentProfileAccount(paymentAccountId)
  }, [paymentAccountId])

  useEffect(() => {
    const shouldIgnoreSessionRevalidation = webviewRuntime.isWebView() && !isPlaygroundMode()
    async function revalidateSession() {
      if (shouldIgnoreSessionRevalidation) return
      if (!lock) {
        lock = true
        try {
          const deviceGeneratedId = Bucket.local.get('device::generated::id')
          const platformId = Bucket.local.get('platform::id')
          await authenticator.revalidateSession(undefined, {
            header: { deviceGeneratedId, platformId }
          })
          if (authenticator.isSessionValid()) setIsSessionValid(true)
          else {
            throw 'Cannot revalidate session. Will log user out.'
          }
        } catch (error) {
          BlackBird.leave({ shouldRedirect: true })
        } finally {
          lock = false
        }
      }
    }

    if (appEnv.isLocal() && ALWAYS_REFRESH_SESSION() === 'true') {
      revalidateSession()
    }
    if (!isSessionValid) {
      revalidateSession()
    }
  }, [isSessionValid, isWebView])

  return {
    deusExMachina,
    isSuccess,
    isError,
    error,
    isWebView: Boolean(isWebView || false)
  }
}

function Error({ title, caption, error }: { title?: string; caption?: string; error?: Error }) {
  const isLoggedIn = Boolean(isomorphicCookies.getAll()['token'])

  useEffect(() => {
    if (error) logger(error)
  }, [error])

  return (
    <TopBarLayout>
      <ErrorContainer
        illustration={<ExclamationIllustration width={400} height={400} />}
        title={isBrowser ? title : 'Erro 500'}
      >
        <Stack space="4rem">
          <Display>Oops, aconteceu algo inesperado.</Display>
          <If
            condition={Boolean(caption)}
            render={() => (
              <Text size="small" color="neutralLow">
                {caption}
              </Text>
            )}
          />
          <Heading size="large">
            Já estamos resolvendo para você tentar novamente daqui a pouco. Se precisar, fale com a gente.
          </Heading>
          <Heading color="neutral">Estamos disponíveis 24h por dia, todos os dias da semana</Heading>
          <If condition={isLoggedIn}>
            <NextLink href="/sair" passHref legacyBehavior>
              <Button as="a" size="large" color="neutral">
                Entrar com outra conta
              </Button>
            </NextLink>
          </If>
        </Stack>
      </ErrorContainer>
    </TopBarLayout>
  )
}

function WebviewError({ error }: { error?: any }) {
  useEffect(() => {
    if (error) webviewRuntime.webviewErrorHandler(error)
  }, [error])
  return <></>
}
